import { Context } from '@microsoft/teams-js';
import { Action, AnyAction, Dispatch } from 'redux';
import { ThunkAction } from 'redux-thunk';
import { ActionPayload } from '../../../../../web/common/actions/base.actions';
import {
  getEmeraldConfigApi,
  updateAppVerCookieForAgavePaneAfterInitApi,
} from '../../../../../web/common/apis/app.api';
import { LogBundleLoadSuccessApi } from '../../../../../web/common/apis/contactUs.api';
import { ecsService } from '../../../../../web/common/apis/ecs.api';
import { appContext } from '../../../../../web/models/getHelp/appContext';
import { License } from '../../../../../web/models/getHelp/license';
import { eventHandlers } from '../../../../../web/utils/events';
import { addOrUpdateAwaMetaTag, captureContentUpdate, reInitializeOneDsAsNeeded, SelfHelpStatus } from '../../../../../web/utils/oneDsUtil';
import * as actionTypes from '../../../../models/actionTypes';
import { UserInfo } from '../../../../models/apiModels';
import { AwaMetaTagType, Environment, SourceApps, SupportedWindowParentOrigins } from '../../../../models/constants/constants';
import { IAppState, ISessionData, SessionData } from '../../../../models/reduxState';
import {
  genericSingleAction,
  getEmeraldConfig,
  getEmeraldConfigError,
  getEmeraldConfigSuccess,
  getEmeraldSupportConfig,
  getEmeraldSupportConfigError,
  getEmeraldSupportConfigSuccess,
  initTeamsSdk,
  setSessionData,
  updateEcsConfiguration,
  updateNSAndVerCookiesForAgavePane,
} from '../../../app/appActions';
import { isGetHelpSelector } from '../../../app/selectors/contactUsSelector';
import { EmeraldConfigRequestModel, EmeraldConfigResponseModel } from '../../models/configModels';
import { licensesSelector } from '../selectors/globalStateSelectors';
import { dispatchUserNoticesFetch } from './contactUsDispatchers';
import { inEmsSession } from '../../../../../common/utils/commonUtilities';

type MicrosoftTeamsSdkType = typeof import('@microsoft/teams-js');

export const dispatchGetEmeraldConfigSuccess = (
  response: EmeraldConfigResponseModel,
  environment: Environment,
  sourceApp: SourceApps
): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    await dispatch(getEmeraldConfigSuccess(response, environment, sourceApp));
    // epic updates for GET_EMERALD_CONFIG_SUCCESS
    const appState = getState();
    const isGetHelp = isGetHelpSelector(appState);
    const appver = appState.GlobalState.appVer;
    if (environment == Environment.agavePane && appver) {
      addOrUpdateAwaMetaTag(AwaMetaTagType.SourceApp, appver);
    }
    if (!isGetHelp) {
      // for gethelp dispatching after we initialize app
      await dispatchUserNoticesFetch()(dispatch, getState, null);
    } else {
      await dispatchGetHelpInit()(dispatch, getState, null);
    }
    await dispatchUpdateAppVerCookieForAgavePaneAfterInit()(dispatch, getState, null);
    // read from response because support configs may not be set in redux yet due to race condition
    if (response.SupportSettingsConfig && response.SupportSettingsConfig.IsLogBundleLoadSuccessEnabled) {
      await dispatchLogBundleLoadSuccess(response)(dispatch, getState, null);
    }
  };
};

export const dispatchGetEmeraldConfig = (
  request: EmeraldConfigRequestModel,
  environment: Environment
): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    dispatch(getEmeraldConfig(request, environment));
    try {
      const response: EmeraldConfigResponseModel = await getEmeraldConfigApi(
        request.Host,
        request.Platform,
        request.Market,
        request.Version,
        request.Namespace ? request.Namespace.toLowerCase() : '',
        request.LCID
      );
      const state = getState();
      await dispatchGetEmeraldConfigSuccess(response, environment, state.GlobalState.sourceApp)(dispatch, getState, null);
    } catch (error) {
      dispatch(getEmeraldConfigError(true));
    }
  };
};

export const dispatchEmeraldSupportConfig = (
  request: EmeraldConfigRequestModel,
  environment: Environment,
  appVer: string
): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    dispatch(getEmeraldSupportConfig(request, environment, appVer));
    try {
      const response: EmeraldConfigResponseModel = await getEmeraldConfigApi(
        request.Host,
        request.Platform,
        request.Market,
        request.Version,
        request.Namespace ? request.Namespace.toLowerCase() : '',
        request.LCID
      );
      const state = getState();
      await dispatchUserNoticesFetch()(dispatch, getState, null);
      dispatch(
        getEmeraldSupportConfigSuccess(response, environment, state.GlobalState.appVer, state.GlobalState.sourceApp)
      );
      // read from response because support configs may not be set in redux yet due to race condition
      if (response.SupportSettingsConfig && response.SupportSettingsConfig.IsLogBundleLoadSuccessEnabled) {
        await dispatchLogBundleLoadSuccess(response)(dispatch, getState, null);
      }
    } catch (error) {
      dispatch(getEmeraldSupportConfigError(true));
    }
  };
};

export const dispatchUpdateAppVerCookieForAgavePaneAfterInit = (): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    if (getState().GlobalState.environment === Environment.agavePane) {
      try {
        const response: UserInfo = await updateAppVerCookieForAgavePaneAfterInitApi();
        const state = getState();
        reInitializeOneDsAsNeeded(response, new SelfHelpStatus(state));
        dispatch(updateNSAndVerCookiesForAgavePane(state.GlobalState.environment));
      } catch (error) {
        const state = getState();
        dispatch(updateNSAndVerCookiesForAgavePane(state.GlobalState.environment));
      }
    }
  };
};

export const dispatchTeamsAsyncCallback = (): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    // Todo: clean up action not updating state - WI 7505056
    dispatch(initTeamsSdk());
    let microsoftTeamsSdk: MicrosoftTeamsSdkType;
    try {
      microsoftTeamsSdk = await import('@microsoft/teams-js');
    } catch (error) {
      microsoftTeamsSdk = (window as any).microsoftTeams;
    }
    microsoftTeamsSdk.initialize(); // if so, initialize the teams SDK
    microsoftTeamsSdk.registerOnThemeChangeHandler((theme: string) => {
      // listen for theme change and update help content when fired
      const url = new URL(document.location.href);
      const context = JSON.parse(url.searchParams.get('context') || '');
      if (context.TeamsTheme) {
        context.TeamsTheme = theme;
      }
      url.searchParams.set('context', JSON.stringify(context));
      window.location.href = url.href;
    });
    // bind callback to microsoftTeamsSdk.getContext
    microsoftTeamsSdk.getContext((context: Context) => {
      // this call used to call two epics asynchronously call updateTeamsHelpIdEpic and loadTeamsSessionDataEpic when TEAMS_SDK_CONTEXT_NOTIFICATION and SET_INITIALIZATION_COMPLETE have been fired.
      // We'll set context to the store and use appInitilizaer componentDidUpdate to fire those two updates asynchronously
      dispatch(genericSingleAction<Context>(actionTypes.TEAMS_SDK_CONTEXT_NOTIFICATION, 'context', context));
    });
  };
};

export const dispatchLoadTeamsSessionData = (context: Context): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, _getState: () => IAppState) => {
    const teamsSessionData: ISessionData = new SessionData(
      `a:${context.userObjectId}`,
      'AAD',
      context.tid || '',
      context.sessionId || ''
    );
    dispatch(setSessionData(teamsSessionData));
  };
};

export const getIframeParentOrigin = (): string | null => {
  const ancestorOrigins = window?.location?.ancestorOrigins;
  return ancestorOrigins && ancestorOrigins.length >= 1 ? ancestorOrigins[0] : null;
};

export const validateParentWindowOriginSupported = (origin?: string): boolean => {
  if (origin === undefined || origin === null || origin.trim().length === 0) {
    // validates input
    return false;
  }
  // returns true only if origin is supported
  return Boolean(SupportedWindowParentOrigins.find((supported: string) => origin.toLowerCase() === supported.toLowerCase()));
};

export const dispatchGetHelpInit = (): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    const notifyParent = (data: string) => {
                  
                                            
               
      if (data && window) {
        if (window.external && 'notify' in window.external) {
          // addresses the transmit message to WebView parent scenario
          window.external.notify(data);
        }
        if (window.chrome && window.chrome.webview && window.chrome.webview.postMessage) {
          // addresses the transmit message to WebView2 parent scenario
          window.chrome.webview.postMessage(data);
        }
        if (validateParentWindowOriginSupported(getIframeParentOrigin()) && window.parent.postMessage) {
          // addresses the transmit message to iframe parent scenario
          window.parent.postMessage(data, getIframeParentOrigin());
        }
      }
    };

    const handleUwpCommand = (command: string, args: unknown) => {
                  
                                                                                                               
               
      return window.dispatchEvent(new CustomEvent(command, { detail: args })).toString();
    };

    (window as any).notifyParent = notifyParent;
    (window as any).handleUwpCommand = handleUwpCommand;

    // Disable ctrl+wheel and ctrl +/- zooming
    window.addEventListener(
      'wheel',
      event => {
        if (event.ctrlKey) {
          event.preventDefault();
        }
      },
      { passive: false }
    );
    window.addEventListener(
      'keydown',
      event => {
        if (
          event.ctrlKey &&
          (event.key === '+' || event.key === '-' || event.key === 'numpad-add' || event.key === 'numpad-subtract')
        ) {
          event.preventDefault();
        }
      },
      true
    );

    const eventHandler = (key?: string | null, data?: any | null) => {
      if (key && key.trim().length !== 0 && data) {
        const handler = eventHandlers[key];
        if (handler) {
          const action = handler(data);
          if (action) {
            if (typeof action === 'function') {
              const thunkAction = action as ThunkAction<void, IAppState, null, Action<ActionPayload>>;
              thunkAction(dispatch, getState, null);
            } else {
              dispatch(action as AnyAction);
            }
          }
        }
      }
    }

    for (const key of Object.keys(eventHandlers)) {
      // addresses the receive message from WebView parent scenario
      window.addEventListener(key, (ev: CustomEvent): void => {
        eventHandler(key, ev.detail);
      });
    }

    if (validateParentWindowOriginSupported(getIframeParentOrigin())) {
      // addresses the receive message from iframe parent scenario
      window.addEventListener(
        'message',
        (event: MessageEvent<{ command: string, data: any }>) => {
          if (validateParentWindowOriginSupported(event.origin)) {
            const message = event.data;
            eventHandler(message.command, message.data);
          }
        }
      );
    }

    const isEms = inEmsSession()
    notifyParent(`{"command" : "EmeraldReady", "context" : {"isEms" : ${isEms}}}`);

    const overrideTags = {
      content: {
        areaName: 'GetHelpInit',
        message: 'notified parent of EmeraldReady command',
        fileName: 'appDispatchers.ts',
        methodName: 'dispatchGetHelpInit',
        isEmsSession: isEms
      },
      behavior: 'OTHER'
    };
    captureContentUpdate(overrideTags);

    notifyParent(`{"command" : "ShowAppRating"}`);
    if (!appContext.appSupportsConfigUpdate) {
      await dispatchUserNoticesFetch()(dispatch, getState, null);
    }
  };
};

export const dispatchLogBundleLoadSuccess = (response: EmeraldConfigResponseModel): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (_: Dispatch, _getState: () => IAppState) => {
    try {
      LogBundleLoadSuccessApi(response);
    } catch (error) {
      // Handle error
    }
  };
};

export const dispatchEcsConfigurationUpdate = (): ThunkAction<void, IAppState, null, Action<ActionPayload>> => {
  return async (dispatch: Dispatch, getState: () => IAppState) => {
    const appState = getState();
    const licenses = licensesSelector(appState) || [];
    const productIds = [...new Set(licenses.filter((x: License) => !!x.ProductId).map((x: License) => x.ProductId))];

    try {
      const ecsConfiguration = await ecsService.getEcsConfiguration(productIds);

      dispatch(updateEcsConfiguration(ecsConfiguration));
    } catch (error) {
      // Handle error
    }
  };
};
